import { useEffect, useRef, useState } from 'react'
import { Box, Fab, Snackbar, SnackbarContent } from '@mui/material'
import AddNewItemCard from './AddNewItemCard'
import ShoppingListGrid, { DropResult } from './ShoppingListGrid'
import WazzapNotifyButton from './WazzappNotifyButton'
import { RefreshOutlined } from '@mui/icons-material'
import { getSupabase } from './supabaseClient'
import { ListMember } from './SignupForm'
import LoadingPageSpinner from './LoadingPageSpinner'
import { v4 as uuidv4 } from 'uuid';

const supabase = getSupabase()

interface Props {
    userMail: string
    listGroupId: string
}
export interface ShoppingListItem {
    id: string;
    text: string;
    index: number;
    list_id: string;
    runPopIn: boolean;
}

export interface DbShoppingListItem {
    id: string;
    text: string;
    index: number;
    list_id: string;
    client_id: string;
}

const toUIItem = (dbItem: DbShoppingListItem): ShoppingListItem => ({
    id: dbItem.id,
    text: dbItem.text,
    index: dbItem.index,
    list_id: dbItem.list_id,
    runPopIn: false // default for items loaded from DB
});


export default function ShoppingList({ userMail, listGroupId }: Props) {
    const [fetchedMembers, setFetchedMembers] = useState(false);
    const [fetchedList, setFetchedList] = useState(false);
    const [fetchedGroupSettigns, setFetchedGroupSettings] = useState(false);
    const [list, setList] = useState<ShoppingListItem[]>([]);
    const [listFriends, setListFriends] = useState<ListMember[]>([]);
    const [onlyFriends, setOnlyFriends] = useState<boolean>(false);
    const [updateMessage, setUpdateMessage] = useState("");
    const [addCardMinimized, setAddCardMinimized] = useState(true);
    const clientId = useRef(uuidv4());

    const toDbItem = (item: ShoppingListItem): DbShoppingListItem => ({
        id: item.id,
        text: item.text,
        index: item.index,
        list_id: item.list_id,
        client_id: clientId.current
    });

    const handleAdd = async (newText: string) => {
        const tempId = uuidv4();

        const optimisticItem: ShoppingListItem = {
            id: tempId,
            text: newText,
            index: list.length,
            list_id: listGroupId,
            runPopIn: true
        };

        // 1. Optimistic update
        setList(prev => [...prev, optimisticItem]);

        // 2. Send SAME ID to Supabase
        const { error } = await supabase
            .from('shopping_items')
            .insert([toDbItem(optimisticItem)]);

        if (error) {
            alert(error.message)

            // rollback
            setList(prev => prev.filter(item => item.id !== tempId));
        }
    };


    const reorderList = (list: ShoppingListItem[], idToMove: string, idToPutBefore: string, addLast: boolean) => {
        const newList = [...list];

        const fromIndex = newList.findIndex(i => i.id === idToMove);
        console.log(fromIndex);
        const toIndex = newList.findIndex(i => i.id === idToPutBefore);
        console.log(toIndex);

        const [moved] = newList.splice(fromIndex, 1);


        if (addLast) {
            console.log("push");
            newList.push(moved);
        } else {
            newList.splice(toIndex < fromIndex ? toIndex : toIndex - 1, 0, moved);
        }


        // reassign indices
        return newList.map((item, index) => ({
            ...item,
            index
        }));
    };


    const handleReorder = async (idToMove: string, dropTo: DropResult) => {
        const updated = reorderList(list, idToMove, dropTo.moveToId, dropTo.addLast);
        // console.log(updated)
        setList(updated.map((i) => {
            if (i.id === idToMove) {
                return { ...i, runPopIn: true }
            }
            return i;
        })); // optimistic UI .map(i => toFalsePop(i))

        const { error } = await supabase
            .from("shopping_items")
            .upsert(updated.map(u => toDbItem(u)));

        if (error) {
            alert(error.message);
            setUpdateMessage("error reorder")
            fetchItems(true); // fallback sync
        }
    };



    const fetchItems = async (writeMessage:boolean) => {
        const { data, error } = await supabase
            .from('shopping_items')
            .select('*')
            .eq('list_id', listGroupId)
            .order('index', { ascending: true })

        if (!error && data) {
            const mess = data.length === list.length ? "Inget att hämta" : "Hämtade uppdateringar";
            if (writeMessage) setUpdateMessage(mess);
            setList(data.map(d => toUIItem(d)))
            if (!fetchedList) setFetchedList(true);
        }
    }

    const fetchGroupMembers = async () => {
        const { data, error } = await supabase
            .from('list_member')
            .select('*')
            .eq('list_id', listGroupId)

        if (error) {
            alert("Kunde inte hämta listgrupp. Testa att ladda om sidan.")
        }

        if (!error && data) {
            setListFriends(data.filter(f => f.email !== userMail))
            if (!fetchedMembers) setFetchedMembers(true);
        }
    }

    const fetchGroupSettings = async () => {
        const { data, error } = await supabase
            .from('list_group')
            .select('for_friends')
            .eq('id', listGroupId)
            .single()

        if (error) {
            alert("Kunde inte hämta listgrupp. Testa att ladda om sidan.")
        }

        if (!error && data) {
            setOnlyFriends(data?.for_friends ?? false)
            if (!fetchedGroupSettigns) setFetchedGroupSettings(true);
        }
    }

    useEffect(() => {
        fetchItems(false)
        fetchGroupMembers()
        fetchGroupSettings()
    }, [])


    const handleDelete = async (id: string) => {
        setList(prev => prev.filter(e => e.id !== id))

        const { error } = await supabase
            .from('shopping_items')
            .delete()
            .eq('id', id)

        if (error) {
            alert("Error deleting item. Please refresh")
        }
    }

    useEffect(() => {
        const channel = supabase
            .channel('shopping_items')
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'shopping_items' },
                (payload) => {
                    const dbItem = payload.new as DbShoppingListItem;
                    if (dbItem.client_id === clientId.current) return;
                    // console.log("realtime insert")
                    const newItem = toUIItem(dbItem);
                    setList(prev => {
                        const exists = prev.some(item => item.id === newItem.id);
                        if (exists) return prev;
                        return [...prev, { ...newItem, runPopIn: true }];
                    });
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'shopping_items' },
                (payload) => {
                    const updated = payload.new as DbShoppingListItem;
                    // if (updated.client_id === clientId.current) return;
                    // console.log("realtime update")
                    const setUpd: ShoppingListItem = updated.client_id === clientId.current ? toUIItem(updated) : { ...toUIItem(updated), runPopIn: true }
                    setList(prev =>
                        prev.map(item =>
                            item.id === updated.id ? setUpd : item
                        ).sort((a, b) => a.index - b.index)
                    );
                }
            )
            .on(
                'postgres_changes',
                { event: 'DELETE', schema: 'public', table: 'shopping_items' },
                (payload) => {
                    const deleted = payload.old as DbShoppingListItem;
                    if (deleted.client_id === clientId.current) return;
                    // console.log("realtime delete")
                    setList(prev => prev.filter(item => item.id !== payload.old.id))
                }
            )
            .subscribe()

        return () => {
            supabase.removeChannel(channel)
        }
    }, [])

    const allFetched = [fetchedMembers, fetchedList, fetchedGroupSettigns].every(Boolean)

    if (!allFetched) {
        return <LoadingPageSpinner />
    }


    return (
        <Box
            sx={{
                minHeight: '85vh',
                backgroundColor: '#f5f5f5',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div className='wrapper'>
                <ShoppingListGrid
                    items={list}
                    onDelete={handleDelete}
                    onCardReorder={handleReorder}
                    height={addCardMinimized ? '11vh' : '7.5vh'}//old 70vh '50vh'
                />
            </div>

            <div
                style={{
                    paddingBottom: '16px',
                    paddingTop: '8px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignContent: 'center',
                    gap: 10, 
                    marginTop:'auto'

                }}
            >

                <WazzapNotifyButton
                    sendToNames={listFriends.map(f => f.name)}
                    sendToPhones={listFriends.map(f => f.phone_nbr)}
                    friendly={onlyFriends}
                />

                <Fab
                    size='small'
                    style={{ backgroundColor: '#ffffff' }}
                    onClick={() => fetchItems(true)}
                >
                    <RefreshOutlined fontSize='small'></RefreshOutlined>
                </Fab>

            </div>



            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <AddNewItemCard onAdd={handleAdd} onMinimize={setAddCardMinimized} defaultExpanded={list.length === 0} betweenFriends={onlyFriends} />
            </div>
            <Snackbar
                open={updateMessage !== ""}
                onClick={() => setUpdateMessage("")}
                autoHideDuration={1000}
                onClose={() => setUpdateMessage("")}
                // sx={{marginBottom:18}}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <SnackbarContent
                    message={updateMessage}
                    sx={{
                        backgroundColor: "#1d4f9bb4",
                        color: "#ffffff",
                        fontWeight: "bold",
                        justifyContent: "center",
                        width: "auto",
                        minWidth: "unset",
                        padding: "6px 16px",
                        borderRadius: "8px",
                    }}
                />
            </Snackbar>
        </Box>
    )
}


